import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaEnvelope, FaPaperPlane } from 'react-icons/fa';

const ContactSection = styled.section`
  padding: 5rem 2rem;
  background: #ffffff;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #0891b2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled(motion.div)``;

const InfoTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const InfoDescription = styled.p`
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 2rem;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const ContactItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(8, 145, 178, 0.1);
  border: 2px solid #0891b2;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0891b2;
  font-size: 1.2rem;
`;

const ContactDetails = styled.div``;

const ContactLabel = styled.div`
  font-size: 0.9rem;
  color: #9ca3af;
  margin-bottom: 0.25rem;
  font-weight: 500;
`;

const ContactValue = styled.div`
  color: #1a1a1a;
  font-weight: 600;
`;

const EmailCTA = styled(motion.div)`
  background: #f8f9fa;
  border: 2px solid #f0f0f0;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: left;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: center;
  }
`;

const CTAButtonLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  color: white;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  box-shadow: 0 4px 15px rgba(8, 145, 178, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(8, 145, 178, 0.3);
  }
`;

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });


  return (
    <ContactSection id="contact">
      <Container ref={ref}>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Let's Light Up Your Player Engagement
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Get a personalized demo and see how Faroflow can transform your casino's customer experience
          </SectionSubtitle>
        </SectionHeader>

        <ContactGrid>
          <ContactInfo
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <InfoTitle>Ready to Get Started?</InfoTitle>
            <InfoDescription>
              Our team of iGaming experts is ready to show you how Faroflow can revolutionize 
              your player support. Schedule a demo to see the platform in action and discuss 
              your specific needs.
            </InfoDescription>
            
            <ContactItems>
              <ContactItem>
                <IconWrapper>
                  <FaEnvelope />
                </IconWrapper>
                <ContactDetails>
                  <ContactLabel>Email Us</ContactLabel>
                  <ContactValue><a href="mailto:hello@faroflow.com" style={{color: 'inherit', textDecoration: 'none'}}>hello@faroflow.com</a></ContactValue>
                </ContactDetails>
              </ContactItem>
              
            </ContactItems>
          </ContactInfo>

          <EmailCTA
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#1a1a1a' }}>Prefer Email?</h3>
            <p style={{ color: '#6b7280', lineHeight: 1.8, marginBottom: '1.5rem' }}>
              We'd love to hear from you. Tell us a bit about your casino and goals,
              and we'll get back to you shortly.
            </p>
            <CTAButtonLink
              href="mailto:hello@faroflow.com?subject=Demo%20Request%20from%20Faroflow%20Website"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaPaperPlane /> Send Email
            </CTAButtonLink>
          </EmailCTA>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

export default Contact; 